import { all, call, delay, put, takeLatest } from 'redux-saga/effects';
import { COMPOSER } from 'src/store/ActionTypes';

import * as api from 'src/store/api';
import { uuid } from 'src/functions';

function* fetchSegments(action) {
  try {
    const { payload: query } = action;
    const segments = yield call(api.fetchSegments, query);
    yield put({
      type: COMPOSER.FETCH_SEGMENTS_SUCCEEDED,
      payload: segments,
    });
  } catch (e) {
    yield put({
      type: COMPOSER.FETCH_SEGMENTS_FAILED,
    });
  }
}

function* fetchStories(action) {
  try {
    const { payload: query } = action;
    const stories = yield call(api.fetchStories, query);
    const storyPromises = stories.map(async (story) => {
      const { slides } = story;
      const slidesPromises = slides.map(async (slide) => {
        if (slide.imageUrl) {
          const presignedUrl = await api.getS3DownloadUrl(slide.imageUrl);
          return {
            ...slide,
            presignedUrl,
          };
        }
        return slide;
      });
      const newSlides = await Promise.all(slidesPromises);
      return {
        ...story,
        slides: newSlides,
      };
    });
    const newStories = yield all(storyPromises);
    yield put({
      type: COMPOSER.FETCH_STORIES_SUCCEEDED,
      payload: newStories,
    });
  } catch (e) {
    yield put({
      type: COMPOSER.FETCH_STORIES_FAILED,
    });
  }
}

function* addStory(action) {
  try {
    const { slides } = action.payload;
    // Check if slides has any images to upload
    const promises = slides.map(async (slide) => {
      if (slide.imageFile) {
        const { imageFile } = slide;
        const newName = `${uuid().substr(0, 8)}/${imageFile.name}`;
        // Get the media upload url
        const [presignedUrl, imageUrl] = await api.getS3UploadUrl(newName);
        // Upload the image
        await api.uploadToS3(presignedUrl, imageFile);
        // Set the media upload url as imageUrl
        // do not return the imageFile
        return {
          ...slide,
          imageUrl,
          imageFile: undefined,
        };
      }
      return slide;
    });
    const newSlides = yield all(promises);
    const story = yield call(api.addStory, { ...action.payload, slides: newSlides });
    yield put({
      type: COMPOSER.ADD_STORY_SUCCEEDED,
      payload: story,
    });
  } catch (e) {
    yield put({
      type: COMPOSER.ADD_STORY_FAILED,
      payload: e,
    });
  } finally {
    delay(500);
    yield put({
      type: COMPOSER.UNSET,
      payload: 'addStoryStatus',
    });
  }
}

function* editStory(action) {
  try {
    const { storyId, slides, ...rest } = action.payload;
    const promises = slides.map(async (slide) => {
      if (slide.imageFile && !slide.imageUrl) {
        const { imageFile } = slide;
        const newName = `${uuid()}/${imageFile.name}`;
        // Get the media upload url
        const [presignedUrl, imageUrl] = await api.getS3UploadUrl(newName);
        // Upload the image
        await api.uploadToS3(presignedUrl, imageFile);
        // Set the media upload url as imageUrl
        // do not return the imageFile
        return {
          ...slide,
          imageUrl,
          imageFile: undefined,
        };
      }
      return slide;
    });
    const newSlides = yield all(promises);
    const story = { ...rest, slides: newSlides };
    yield call(api.editStory, storyId, story);
    yield put({
      type: COMPOSER.EDIT_STORY_SUCCEEDED,
    });
  } catch (e) {
    yield put({
      type: COMPOSER.EDIT_STORY_FAILED,
      payload: e,
    });
  } finally {
    delay(500);
    yield put({
      type: COMPOSER.UNSET,
      payload: 'editStoryStatus',
    });
  }
}

function* deleteStory(action) {
  try {
    yield call(api.deleteStory, action.payload);
    yield put({
      type: COMPOSER.DELETE_STORY_SUCCEEDED,
    });
  } catch (e) {
    yield put({
      type: COMPOSER.DELETE_STORY_FAILED,
      payload: e,
    });
  } finally {
    delay(500);
    yield put({
      type: COMPOSER.UNSET,
      payload: 'deleteStoryStatus',
    });
  }
}

function* fetchSegmentsSaga() {
  yield takeLatest(COMPOSER.FETCH_SEGMENTS, fetchSegments);
}

function* fetchStoriesSaga() {
  yield takeLatest(COMPOSER.FETCH_STORIES, fetchStories);
}

function* editStorySaga() {
  yield takeLatest(COMPOSER.EDIT_STORY, editStory);
}

function* addStorySaga() {
  yield takeLatest(COMPOSER.ADD_STORY, addStory);
}

function* deleteStorySaga() {
  yield takeLatest(COMPOSER.DELETE_STORY, deleteStory);
}

export default [fetchSegmentsSaga(), fetchStoriesSaga(), addStorySaga(), editStorySaga(), deleteStorySaga()];
